// src/pages/customer/Orders.jsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getOrdersByUser } from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import { format } from "date-fns";
import { FiPackage, FiArrowRight, FiChevronRight } from "react-icons/fi";

const STATUS_COLORS = {
  Pending:   "bg-yellow-100 text-yellow-700 border-yellow-200",
  Confirmed: "bg-blue-100 text-blue-700 border-blue-200",
  Shipped:   "bg-indigo-100 text-indigo-700 border-indigo-200",
  Delivered: "bg-green-100 text-green-700 border-green-200",
  Cancelled: "bg-red-100 text-red-700 border-red-200",
};

const STATUS_DOT = {
  Pending:   "bg-yellow-400",
  Confirmed: "bg-blue-400",
  Shipped:   "bg-indigo-400",
  Delivered: "bg-green-500",
  Cancelled: "bg-red-400",
};

// Build a readable "30 kg" / "5 pack(s)" label for a given order line item
function getQtyLabel(item) {
  if (!item) return null;
  if (item.unit === "pack") return item.packSize ? `${item.packSize} pack(s)` : null;
  return item.weightSize ? `${item.weightSize} kg` : null;
}

export default function Orders() {
  const { user } = useAuth();
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getOrdersByUser(user.uid).then((o) => { setOrders(o); setLoading(false); });
  }, [user]);

  if (loading) return <Spinner center />;

  return (
    <div className="min-h-screen" style={{ background: "#f7f6f2" }}>
      <Helmet>
        <title>My Orders | Tripura Agro</title>
        <meta
          name="description"
          content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        {/* ── Header ── */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Orders</h1>
            <p className="text-sm text-gray-400 mt-1">
              {orders.length > 0
                ? `${orders.length} order${orders.length !== 1 ? "s" : ""} placed`
                : "Track and manage your purchases"}
            </p>
          </div>
          {orders.length > 0 && (
            <div className="hidden sm:flex w-11 h-11 rounded-2xl bg-primary-50 items-center justify-center">
              <FiPackage className="w-5 h-5 text-primary-600" />
            </div>
          )}
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-gray-100">
            <div className="w-20 h-20 bg-gray-50 rounded-3xl flex items-center justify-center mx-auto mb-5">
              <FiPackage className="w-9 h-9 text-gray-300" />
            </div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">No orders yet</h3>
            <p className="text-gray-400 text-sm mb-6">Start shopping to place your first order!</p>
            <Link to="/products" className="btn-primary">Shop Now</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const firstItem  = order.items?.[0];
              const extraCount = (order.items?.length || 0) - 1;

              return (
                <Link
                  key={order.id}
                  to={`/orders/${order.id}`}
                  className="group block bg-white rounded-2xl border border-gray-100 overflow-hidden
                             hover:border-primary-200 hover:shadow-lg hover:shadow-green-900/5
                             hover:-translate-y-0.5 transition-all duration-200"
                >
                  {/* ── Top row: order id / date / status ── */}
                  <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-50">
                    <div className="min-w-0">
                      <p className="font-mono text-[11px] text-gray-400 tracking-wide">
                        #{order.id.slice(0, 12).toUpperCase()}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {order.createdAt?.toDate
                          ? format(order.createdAt.toDate(), "dd MMM yyyy, hh:mm a")
                          : "Just now"}
                      </p>
                    </div>
                    <span
                      className={`flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border flex-shrink-0
                        ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-700 border-gray-200"}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[order.status] || "bg-gray-400"}`} />
                      {order.status}
                    </span>
                  </div>

                  {/* ── Middle: product preview ── */}
                  <div className="flex items-center gap-4 px-5 py-4">
                    {/* Main product image */}
                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex-shrink-0">
                      {firstItem?.image ? (
                        <img src={firstItem.image} alt={firstItem.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl">🌱</div>
                      )}
                    </div>

                    {/* Product name + details */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm leading-snug line-clamp-1">
                        {firstItem?.name || "Order item"}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {getQtyLabel(firstItem) ? `${getQtyLabel(firstItem)} · ` : ""}
                        Qty {firstItem?.qty ?? 1}
                        {extraCount > 0 && (
                          <span className="text-primary-600 font-medium"> · +{extraCount} more item{extraCount !== 1 ? "s" : ""}</span>
                        )}
                      </p>
                    </div>

                    {/* Stacked extra thumbnails (desktop only) */}
                    {order.items?.length > 1 && (
                      <div className="hidden sm:flex -space-x-2 flex-shrink-0">
                        {order.items.slice(1, 4).map((item, i) => (
                          <div key={i} className="w-9 h-9 rounded-lg overflow-hidden bg-gray-50 border-2 border-white shadow-sm">
                            {item.image
                              ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                              : <div className="w-full h-full flex items-center justify-center text-sm">🌱</div>}
                          </div>
                        ))}
                        {order.items.length > 4 && (
                          <div className="w-9 h-9 rounded-lg bg-gray-100 border-2 border-white shadow-sm flex items-center justify-center text-[10px] font-semibold text-gray-500">
                            +{order.items.length - 4}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Bottom row: total + CTA ── */}
                  <div className="flex items-center justify-between px-5 py-3 bg-gray-50/60 border-t border-gray-50">
                    <p className="text-xs text-gray-400">
                      {order.items?.length || 0} item{order.items?.length !== 1 ? "s" : ""}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-primary-600 text-base">₹{Number(order.total || 0).toFixed(2)}</span>
                      <FiChevronRight className="w-4 h-4 text-gray-300 group-hover:text-primary-500 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}